import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Trash2, Star, Film, FolderSearch,
  ClipboardList, ChevronDown, Users as UsersIcon,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

// Ordered to match the actual content pipeline: a request comes in, gets pulled into
// Radarr/Sonarr automatically (webhook-driven import — see Settings), or uploaded/scanned in
// directly. No codec-check/transcode step anymore — playback is Kodi-only, which handles any
// codec natively. Everything else is a standing utility, not a step content passes through.
// Radarr/Sonarr imports are no longer menu items: new downloads import automatically via the
// Radarr/Sonarr webhooks, and the Library page's "Maintain Library" imports every completed item
// in its one pass. The /admin/radarr and /admin/sonarr pages still exist (reachable by URL) for
// one-off selective per-title imports, just off the menu.
const PIPELINE_LINKS = [
  { to: "/requests", label: "Review Requests", icon: ClipboardList, testId: "admin-requests-link" },
  { to: "/admin/upload", label: "Upload Movie", icon: Film, testId: "admin-upload-link" },
  { to: "/admin/library", label: "Library", icon: FolderSearch, testId: "admin-library-link" },
];

const UTILITY_LINKS = [
  { to: "/admin/users", label: "Users", icon: UsersIcon, testId: "admin-users-link" },
];

function AdminMenu({ label, links }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2 text-xs uppercase tracking-[0.2em] border border-white/10"
        data-testid={`admin-menu-${label.toLowerCase()}`}
      >
        {label} <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[220px] bg-[#0F0F0F] border border-[#222] z-20 shadow-lg" data-testid={`admin-menu-${label.toLowerCase()}-panel`}>
          {links.map(({ to, label: linkLabel, icon: Icon, testId }) => (
            <Link
              key={to} to={to} onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white hover:bg-white/5 border-b border-[#222] last:border-b-0"
              data-testid={testId}
            >
              <Icon size={14} strokeWidth={1.5} /> {linkLabel}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [movies, setMovies] = useState([]);
  const [shows, setShows] = useState([]);

  const load = async () => {
    const [{ data: m }, { data: s }] = await Promise.all([
      api.get("/movies"),
      api.get("/shows").catch(() => ({ data: [] })),
    ]);
    setMovies(m); setShows(s);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this movie permanently?")) return;
    await api.delete(`/movies/${id}`);
    toast.success("Removed");
    load();
  };

  const toggleFeatured = async (m) => {
    await api.patch(`/movies/${m.id}`, { featured: !m.featured });
    load();
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="admin-page">
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Library management</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">Admin</h1>
        <p className="text-[#8A8A8A] mt-4">Manage your library, requests, integrations.</p>

        <div className="mt-10 flex flex-wrap gap-3">
          <AdminMenu label="Pipeline" links={PIPELINE_LINKS} />
          <AdminMenu label="Utilities" links={UTILITY_LINKS} />
        </div>

        <div className="mt-12 border border-[#222]">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-[#222] text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">
            <span className="col-span-4">Title</span>
            <span className="col-span-1">Year</span>
            <span className="col-span-1">Rating</span>
            <span className="col-span-4">Source</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {movies.map((m) => (
            <div key={m.id} className="grid grid-cols-12 items-center px-5 py-4 border-b border-[#222] last:border-b-0 hover:bg-[#0F0F0F] transition-colors" data-testid={`admin-movie-row-${m.id}`}>
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <img src={m.poster_url} alt="" className="w-10 h-14 object-cover" />
                <span className="text-white truncate">{m.title}</span>
              </div>
              <span className="col-span-1 text-[#8A8A8A] text-sm">{m.year}</span>
              <span className="col-span-1 text-[#8A8A8A] text-sm">{m.rating}</span>
              <span className="col-span-4 text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">{m.storage_type}</span>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => toggleFeatured(m)} className={`${m.featured ? "text-[#D9381E]" : "text-[#8A8A8A]"} hover:text-[#ED4B32] transition-colors`} aria-label="Feature" data-testid={`feature-${m.id}`}>
                  <Star size={16} strokeWidth={1.5} fill={m.featured ? "#D9381E" : "none"} />
                </button>
                <button onClick={() => remove(m.id)} className="text-[#8A8A8A] hover:text-[#fca5a5] transition-colors" aria-label="Delete" data-testid={`delete-${m.id}`}>
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {shows.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-4">Series</h2>
            <div className="border border-[#222]">
              <div className="grid grid-cols-12 px-5 py-3 border-b border-[#222] text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">
                <span className="col-span-6">Title</span>
                <span className="col-span-2">Year</span>
                <span className="col-span-2">Episodes</span>
                <span className="col-span-2 text-right">Actions</span>
              </div>
              {shows.map((s) => (
                <div key={s.id} className="grid grid-cols-12 items-center px-5 py-4 border-b border-[#222] last:border-b-0 hover:bg-[#0F0F0F] transition-colors" data-testid={`admin-show-row-${s.id}`}>
                  <div className="col-span-6 flex items-center gap-3 min-w-0">
                    <img src={s.poster_url} alt="" className="w-10 h-14 object-cover" />
                    <span className="text-white truncate">{s.title}</span>
                  </div>
                  <span className="col-span-2 text-[#8A8A8A] text-sm">{s.year}</span>
                  <span className="col-span-2 text-[#8A8A8A] text-sm">{s.season_count}s · {s.episode_count}e</span>
                  <div className="col-span-2 flex justify-end">
                    <a href={`/admin/shows/${s.id}/episodes`} className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#D9381E] hover:text-[#D9381E] text-[#8A8A8A] px-3 py-1 transition-colors" data-testid={`manage-episodes-${s.id}`}>
                      Manage Episodes
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
